import { useCallback } from 'react';
import { FieldValues, UseFormSetError, UseFormClearErrors } from 'react-hook-form';
import { handleFormErrors } from '@utils/formErrorHandler';

/**
 * Custom hook for handling form errors consistently across the application
 * @param setError - React Hook Form's setError function
 * @param clearErrors - React Hook Form's clearErrors function
 * @returns Object with error handling utilities
 */
export function useFormErrorHandler<T extends FieldValues>(
  setError: UseFormSetError<T>,
  clearErrors: UseFormClearErrors<T>
) {
  /**
   * Handles errors from async form submissions
   * Automatically maps server errors to form fields
   */
  const handleSubmissionError = useCallback(
    (error: unknown) => {
      handleFormErrors(error, setError);
    },
    [setError]
  );

  /**
   * Clears all form errors (useful before submission)
   */
  const clearAllErrors = useCallback(() => {
    clearErrors();
  }, [clearErrors]);

  /**
   * Wrapper function for async form submissions with error handling
   */
  const withErrorHandling = useCallback(
    async (submitFn: () => Promise<void>) => {
      try {
        clearAllErrors();
        await submitFn();
      } catch (error) {
        handleSubmissionError(error);
      }
    },
    [clearAllErrors, handleSubmissionError]
  );

  return {
    handleSubmissionError,
    clearAllErrors,
    withErrorHandling,
  };
} 